import { SelectionChangedEvent } from "ag-grid-community";
import { CounterCustomerChargeEntity } from "domain/entity/CounterCustomerCharge/CounterCustomerChargeEntity";
import { CounterCustomerChargeMaintenanceConstant } from "presentation/constant/CounterCustomerCharge/CounterCustomerChargeMaintenanceConstant";
import { COUNTER_CUSTOMER_CHARGE_COL_DEF } from "presentation/constant/CounterCustomerCharge/UpdateAgentDeliveryDateColumnDefinition";
import { useCounterCustomerChargeMaintenanceVM } from "presentation/hook/CounterCustomerCharge/useCounterCustomerChargeMaintenanceVM";
import { useMessageBarVM } from "presentation/hook/useMessageBar";
import { AgentDeliveryDateState } from "presentation/model/CounterCustomerCharge/CounterCustomerChargeMaintenanceModel";
import { useCounterCustomerChargeMaintenanceTracked } from "presentation/store/CounterCustomerCharge/CounterCustomerChargeMaintenanceProvider";
import { CriteriaItemContainer } from "presentation/view/components/CriteriaItemContainer";
import DatePickerComponent from "presentation/view/components/DatePickerComponent";
import { HeaderTitle } from "presentation/view/components/HeaderWithBackButton/HeaderTitle";
import NbisTable from "presentation/view/components/TableWrapper/NbisTable";
import { TableWrapper } from "presentation/view/components/TableWrapper/TableWrapper";
import { memo, useCallback, useMemo, useRef, useState } from "react";
import { HPHButton, Loader } from "veronica-ui-component/dist/component/core";
import { SidebarActionBar, Sidebarheader } from "veronica-ui-component/dist/component/core/styled/uiFramework.styled";

const UpdateAgentDeliveryDatePanel = () => {
    const [counterCustomerChargeState] = useCounterCustomerChargeMaintenanceTracked();
    const counterCustomerChargeVM = useCounterCustomerChargeMaintenanceVM();
    const COUNTER_CUSTOMER_CHARGE_CONSTANT = CounterCustomerChargeMaintenanceConstant.Detail;
    const { agentDeliveryDateMap, selectedAgentDeliveryDateMap, editingEntity } = counterCustomerChargeState;
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const messageBarVM = useMessageBarVM();
    const gridRef: any = useRef(null);

    const vaildation = useCallback(() => {
        const currentDate = new Date();//dd-MM-yyyy HH:mm:ss
        currentDate.setHours(0, 0, 0, 0);
        let msg = null;
        let hasError = false;
        agentDeliveryDateMap.forEach((value) => {
            if (hasError) return;
            if (!value) {
                msg = "Please input the Agent delivery date time.";
                hasError = true;
                return;
            }
            const deliveryDate = new Date(value);
            if (deliveryDate < currentDate) {
                msg = "The Agent delivery date time should not be earlier than the current system date.";
                hasError = true;
                return;
            }
        });
        if (msg) {
            messageBarVM.showError(msg);
            return false;
        }

        return true;
    }, [agentDeliveryDateMap, messageBarVM]);

    const handleSaveClick = useCallback(async () => {
        if (!vaildation()) {
            return;
        }
        setIsLoading(true);
        let entityList: CounterCustomerChargeEntity[] = [];
        Array.from(agentDeliveryDateMap).sort().map(([key, value]) => (
            counterCustomerChargeState.tableData?.filter(item => item.cntrNo === key && item.dtlState !== "DI" && item.dtlState !== "I" && item.adjType !== "Cancel Only")?.map(item => (
                entityList.push({
                    ...item,
                    agentDeliveryDate: value
                })
            ))
        ));

        counterCustomerChargeVM.onSave(entityList).then((res) => {
            setIsLoading(false);
            counterCustomerChargeVM.onSearchCounterCustomer(counterCustomerChargeState.searchCriteria).finally(() => {
                if (res && res.success) {
                    messageBarVM.showSuccess('Save Successfully.');
                    counterCustomerChargeVM.onMoreClose();
                } else {
                    messageBarVM.showError('Save Failed.');
                }
            });
        });

    }, [counterCustomerChargeVM, messageBarVM, counterCustomerChargeState.searchCriteria, vaildation, agentDeliveryDateMap, counterCustomerChargeState.tableData]);

    const handClose = useCallback(() => {
        counterCustomerChargeVM.onMoreClose();
    }, [counterCustomerChargeVM]);

    const allRows = useMemo<AgentDeliveryDateState[]>(() => {
        if (!agentDeliveryDateMap || agentDeliveryDateMap.size === 0) {
            return [];
        }
        return Array.from(agentDeliveryDateMap)
            .map(([containerNo, agentDeliveryDate]) => ({
                cntrNo: containerNo,
                agentDeliveryDate: agentDeliveryDate,
            }))
            .sort((a, b) => a.cntrNo.localeCompare(b.cntrNo));
    }, [agentDeliveryDateMap]);

    const handleSelectionChange = useCallback((e: SelectionChangedEvent) => {
        const selectedRows = e.api.getSelectedRows();
        counterCustomerChargeVM.updateSelectedAgentDeliveryDateRows(selectedRows);
    }, [counterCustomerChargeVM])

    const handApply = useCallback(() => {
        if (!selectedAgentDeliveryDateMap || selectedAgentDeliveryDateMap.size === 0) {
            messageBarVM.showError("Please select record(s).");
            return;
        }
        if (!editingEntity.agentDeliveryDate) {
            messageBarVM.showError("Please input the Agent Delivery date time.");
            return;
        }
        counterCustomerChargeVM.onApplyAgentDeliveryClick(agentDeliveryDateMap, selectedAgentDeliveryDateMap);
    }, [counterCustomerChargeVM, editingEntity.agentDeliveryDate, messageBarVM, agentDeliveryDateMap, selectedAgentDeliveryDateMap]);


    const updateAgentDeliveryDatePanelTable =
        <NbisTable
            id='update-paid-through-date-table'
            isNewColumnSetting={false}
            columns={COUNTER_CUSTOMER_CHARGE_COL_DEF?.slice()}
            data={allRows ?? []}
            showPaginator={false}
            editable={false}
            showAddIcon={false}
            showUploadIcon={false}
            showDeleteButton={false}
            showReloadIcon={false}
            selectionMode={false}
            isRowHighligted={true}
            showPreferenceMenu={false}
            showSidebar={false}
            handleSelectionChanged={handleSelectionChange}
            gridHeight="customHeight"
            customHeight="calc(40vh)"
            ref={gridRef}
            rowSelection={"multiple"}
        />
    const memoAgentDeliveryDate = useMemo(() => {
        if (!agentDeliveryDateMap || agentDeliveryDateMap.size === 0) {
            return null;
        }

        return (
            <>
                <CriteriaItemContainer>
                    <div className="im-flex-row-item" style={{ width: "220px", marginBottom: "24px", marginLeft: "15px" }}>
                        <DatePickerComponent
                            disabled={false}
                            label={COUNTER_CUSTOMER_CHARGE_CONSTANT.AGENT_DELIVERY_DATE}
                            width="220px"
                            date={editingEntity?.agentDeliveryDate}
                            fieldName={"agentDeliveryDate"}
                            errorMessage=""
                            onDateChange={counterCustomerChargeVM.onCounterCustomerFieldChange}
                        />
                    </div>
                    <HPHButton label={'Apply'} size={'Small'} theme={'Secondary'} onClick={handApply} />
                </CriteriaItemContainer>
            </>
        );
    }, [COUNTER_CUSTOMER_CHARGE_CONSTANT.AGENT_DELIVERY_DATE, counterCustomerChargeVM, editingEntity?.agentDeliveryDate, handApply, agentDeliveryDateMap]);

    return (<div className='side-form-content-left-wrapper' style={{ padding: "20px 20px 20px 0px" }}>
        <div className={'flex-row'}>
            <div className={'flex-row-item flex-row-item-full-width'}>
                <Sidebarheader>
                    <HeaderTitle>{"Update Agent Delivery Date"}</HeaderTitle>
                </Sidebarheader>
            </div>
        </div>

        {isLoading && <Loader Indicator="Spinner" size="Medium" />}

        <div style={{ marginTop: "-50px" }}>
            <TableWrapper>
                <div style={{ marginLeft: "15px" }}>{updateAgentDeliveryDatePanelTable}</div>
            </TableWrapper>
        </div>

        <div className={'add-edit-form'} style={{ maxHeight: '75vh', height: '75vh', overflow: 'auto' }}>
            <div className="flex-row-auto">
                <div className={'flex-row-item'}>
                    {memoAgentDeliveryDate}
                </div>
            </div>
        </div>
        <SidebarActionBar>
            <HPHButton label={'Close'} size={'Small'} theme={'Secondary'} onClick={handClose} />
            <HPHButton label={'Save'} size={'Small'} theme={'Primary'} onClick={() => handleSaveClick()} />
        </SidebarActionBar>
    </div>)
}

export default memo(UpdateAgentDeliveryDatePanel);